import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import autopilotCampaignService from '../../services/autopilotCampaign';

function ApprovalQueue({ campaignId = null, onRefresh }) {
    const [queueItems, setQueueItems] = useState([]);
    const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
    const [isLoading, setIsLoading] = useState(false);
    const [selectedItems, setSelectedItems] = useState([]);
    const [statusFilter, setStatusFilter] = useState('pending');
    const [expandedItem, setExpandedItem] = useState(null);
    const [editingOutreach, setEditingOutreach] = useState(null);

    const fetchQueue = useCallback(async () => {
        setIsLoading(true);
        try {
            const params = { status: statusFilter };
            if (campaignId) params.campaignId = campaignId;

            const data = await autopilotCampaignService.getQueue(params);
            setQueueItems(data.queueItems || []);
            setCounts(data.counts || { pending: 0, approved: 0, rejected: 0 });
        } catch (error) {
            console.error('Error fetching queue:', error);
            toast.error('Failed to load approval queue');
        } finally {
            setIsLoading(false);
        }
    }, [campaignId, statusFilter]);

    useEffect(() => {
        fetchQueue();
    }, [fetchQueue]);

    const handleApprove = async (id, sendEmail = true) => {
        try {
            await autopilotCampaignService.approveCandidate(id, { sendEmail });
            toast.success('Candidate approved and email sent!');
            fetchQueue();
            if (onRefresh) onRefresh();
        } catch (error) {
            toast.error('Failed to approve candidate');
        }
    };

    const handleReject = async (id, reason = '') => {
        try {
            await autopilotCampaignService.rejectCandidate(id, reason);
            toast.success('Candidate rejected');
            fetchQueue();
            if (onRefresh) onRefresh();
        } catch (error) {
            toast.error('Failed to reject candidate');
        }
    };

    const handleBulkApprove = async () => {
        if (selectedItems.length === 0) return;
        try {
            const result = await autopilotCampaignService.bulkApprove(selectedItems);
            toast.success(`Approved ${result.results.approved} candidates`);
            setSelectedItems([]);
            fetchQueue();
            if (onRefresh) onRefresh();
        } catch (error) {
            toast.error('Failed to bulk approve');
        }
    };

    const handleBulkReject = async () => {
        if (selectedItems.length === 0) return;
        try {
            await autopilotCampaignService.bulkReject(selectedItems);
            toast.success('Candidates rejected');
            setSelectedItems([]);
            fetchQueue();
            if (onRefresh) onRefresh();
        } catch (error) {
            toast.error('Failed to bulk reject');
        }
    };

    const handleUpdateOutreach = async (id, outreachData) => {
        try {
            await autopilotCampaignService.updateOutreach(id, outreachData);
            toast.success('Outreach updated');
            setEditingOutreach(null);
            fetchQueue();
        } catch (error) {
            toast.error('Failed to update outreach');
        }
    };

    const toggleSelection = (id) => {
        setSelectedItems(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    const selectAll = () => {
        setSelectedItems(queueItems.map(item => item._id));
    };

    const getMatchScoreColor = (score) => {
        if (score >= 90) return 'bg-green-100 text-green-800';
        if (score >= 80) return 'bg-blue-100 text-blue-800';
        if (score >= 70) return 'bg-yellow-100 text-yellow-800';
        return 'bg-orange-100 text-orange-800';
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'high': return 'bg-red-100 text-red-700';
            case 'medium': return 'bg-yellow-100 text-yellow-700';
            default: return 'bg-gray-100 text-gray-700';
        }
    };

    return (
        <div className="bg-white rounded-lg shadow">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">Candidate Approval Queue</h2>
                        <p className="text-sm text-gray-500">Review and approve candidates for outreach</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">
                            {counts.pending} Pending
                        </span>
                        <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                            {counts.approved} Approved
                        </span>
                    </div>
                </div>

                {/* Filters */}
                <div className="mt-4 flex items-center gap-4">
                    <div className="flex gap-2">
                        {['pending', 'approved', 'rejected', 'all'].map(status => (
                            <button
                                key={status}
                                onClick={() => setStatusFilter(status)}
                                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${statusFilter === status
                                        ? 'bg-indigo-600 text-white'
                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}
                            >
                                {status.charAt(0).toUpperCase() + status.slice(1)}
                            </button>
                        ))}
                    </div>

                    {selectedItems.length > 0 && (
                        <div className="flex items-center gap-2 ml-auto">
                            <span className="text-sm text-gray-600">{selectedItems.length} selected</span>
                            <button
                                onClick={handleBulkApprove}
                                className="px-3 py-1 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700"
                            >
                                Approve All
                            </button>
                            <button
                                onClick={handleBulkReject}
                                className="px-3 py-1 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700"
                            >
                                Reject All
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Queue List */}
            <div className="divide-y divide-gray-100">
                {isLoading ? (
                    <div className="p-8 text-center text-gray-500">Loading...</div>
                ) : queueItems.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                        No candidates in queue
                    </div>
                ) : (
                    queueItems.map(item => (
                        <div
                            key={item._id}
                            className={`p-4 hover:bg-gray-50 ${selectedItems.includes(item._id) ? 'bg-indigo-50' : ''
                                }`}
                        >
                            <div className="flex items-start gap-4">
                                {statusFilter === 'pending' && (
                                    <input
                                        type="checkbox"
                                        checked={selectedItems.includes(item._id)}
                                        onChange={() => toggleSelection(item._id)}
                                        className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600"
                                    />
                                )}

                                <div className="flex-1">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div>
                                                <h3 className="font-semibold text-gray-900">
                                                    {item.candidateId?.fullName || 'Unknown'}
                                                </h3>
                                                <p className="text-sm text-gray-500">{item.candidateId?.email}</p>
                                            </div>
                                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getMatchScoreColor(item.matchScore)}`}>
                                                {item.matchScore}% Match
                                            </span>
                                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(item.priority)}`}>
                                                {item.priority}
                                            </span>
                                        </div>

                                        {/* Action Buttons */}
                                        {item.status === 'pending' && (
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => setExpandedItem(expandedItem === item._id ? null : item._id)}
                                                    className="p-2 text-gray-400 hover:text-gray-600"
                                                    title="View Details"
                                                >
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                    </svg>
                                                </button>
                                                <button
                                                    onClick={() => handleReject(item._id)}
                                                    className="px-4 py-2 border border-red-300 text-red-600 rounded-lg text-sm font-medium hover:bg-red-50"
                                                >
                                                    Reject
                                                </button>
                                                <button
                                                    onClick={() => handleApprove(item._id, false)}
                                                    className="px-4 py-2 border border-green-300 text-green-600 rounded-lg text-sm font-medium hover:bg-green-50"
                                                >
                                                    Approve
                                                </button>
                                                <button
                                                    onClick={() => handleApprove(item._id, true)}
                                                    className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 flex items-center gap-1"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                                    </svg>
                                                    Approve & Send
                                                </button>
                                            </div>
                                        )}

                                        {item.status !== 'pending' && (
                                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${item.status === 'approved' ? 'bg-green-100 text-green-700' :
                                                    item.status === 'rejected' ? 'bg-red-100 text-red-700' :
                                                        'bg-gray-100 text-gray-700'
                                                }`}>
                                                {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                                            </span>
                                        )}
                                    </div>

                                    {/* Match reason */}
                                    {item.matchReason && (
                                        <p className="mt-2 text-sm text-gray-600">{item.matchReason}</p>
                                    )}

                                    {/* Skills */}
                                    {item.candidateId?.skills && (
                                        <div className="mt-2 flex flex-wrap gap-1">
                                            {item.candidateId.skills.slice(0, 5).map((skill, idx) => (
                                                <span key={idx} className="px-2 py-0.5 bg-blue-50 text-blue-600 rounded text-xs">
                                                    {skill}
                                                </span>
                                            ))}
                                        </div>
                                    )}

                                    {/* Expanded Details */}
                                    {expandedItem === item._id && (
                                        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                                            <h4 className="font-medium text-gray-900 mb-2">Generated Outreach</h4>
                                            {editingOutreach === item._id ? (
                                                <div className="space-y-3">
                                                    <input
                                                        type="text"
                                                        defaultValue={item.generatedOutreach?.subject}
                                                        id={`subject-${item._id}`}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                                        placeholder="Subject"
                                                    />
                                                    <textarea
                                                        defaultValue={item.generatedOutreach?.editedBody || item.generatedOutreach?.body}
                                                        id={`body-${item._id}`}
                                                        rows={6}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                                        placeholder="Email body"
                                                    />
                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={() => handleUpdateOutreach(item._id, {
                                                                subject: document.getElementById(`subject-${item._id}`).value,
                                                                body: document.getElementById(`body-${item._id}`).value
                                                            })}
                                                            className="px-3 py-1 bg-indigo-600 text-white rounded text-sm"
                                                        >
                                                            Save
                                                        </button>
                                                        <button
                                                            onClick={() => setEditingOutreach(null)}
                                                            className="px-3 py-1 bg-gray-200 text-gray-700 rounded text-sm"
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div>
                                                    <p className="text-sm font-medium text-gray-700">
                                                        Subject: {item.generatedOutreach?.subject}
                                                    </p>
                                                    <p className="mt-2 text-sm text-gray-600 whitespace-pre-wrap">
                                                        {item.generatedOutreach?.editedBody || item.generatedOutreach?.body}
                                                    </p>
                                                    <button
                                                        onClick={() => setEditingOutreach(item._id)}
                                                        className="mt-2 text-sm text-indigo-600 hover:text-indigo-700"
                                                    >
                                                        Edit Outreach
                                                    </button>
                                                </div>
                                            )}

                                            {/* Email sequence status */}
                                            {item.emailSequence?.initialEmailSent && (
                                                <div className="mt-4 pt-4 border-t border-gray-200">
                                                    <h4 className="font-medium text-gray-900 mb-2">Email Sequence</h4>
                                                    <div className="flex items-center gap-4 text-sm">
                                                        <span className="text-green-600">
                                                            Initial email sent {new Date(item.emailSequence.initialEmailSentAt).toLocaleDateString()}
                                                        </span>
                                                        <span>Follow-ups sent: {item.emailSequence.followUpsSent?.length || 0}</span>
                                                        {item.emailSequence.nextFollowUpAt && (
                                                            <span className="text-gray-500">
                                                                Next follow-up: {new Date(item.emailSequence.nextFollowUpAt).toLocaleDateString()}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Select All */}
            {statusFilter === 'pending' && queueItems.length > 0 && (
                <div className="px-6 py-3 border-t border-gray-200 bg-gray-50">
                    <button
                        onClick={selectAll}
                        className="text-sm text-indigo-600 hover:text-indigo-700"
                    >
                        Select all {queueItems.length} candidates
                    </button>
                </div>
            )}
        </div>
    );
}

export default ApprovalQueue;
